import { useRouterState } from '@tanstack/react-router';
import {
  type AIContext,
  type SidePanelMode,
  useRouteSidePanelStore,
} from '@/shared/stores/side-panel-store';
import { AIMatchingProjectDetailSidePanel } from '@/widgets/ai-matching-results/project-detail-side-panel/ui/AIMatchingProjectDetailSidePanel';
import { AssessmentComparisonSidePanel } from '@/widgets/assessment/assessment-comparison/AssessmentComparisonSidePanel';
import { AccountingTypesSidePanel } from '@/widgets/master-management/ui/accounting-types-create-side-panel/AccountingTypesCreateSidePanel';
import { ExpenditureItemsSidePanel } from '@/widgets/master-management/ui/expenditure-items-side-panel/ExpenditureItemsSidePanel';
import { OrganizationsSidePanel } from '@/widgets/master-management/ui/organizations-create-side-panel/OrganizationsSidePanel';
import { ExpenditureEditSidePanel } from '@/widgets/settlement-statistics-converter/ui/expenditure-edit-side-panel/ExpenditureEditSidePanel';
import { MasterCreateSidePanel } from '@/widgets/settlement-statistics-converter/ui/master-create-side-panel/MasterCreateSidePanel';
import { RevenueAppropriationExpenditureSidePanel } from '@/widgets/settlement-statistics-converter/ui/revenue-appropriation-expenditure-side-panel/RevenueAppropriationExpenditureSidePanel';
import { RevenueEditSidePanel } from '@/widgets/settlement-statistics-converter/ui/revenue-edit-side-panel/RevenueEditSidePanel';
import { UserManagementSidePanel } from '@/widgets/user-management/ui/user-management-side-panel/UserManagementSidePanel';
import type { RouteSidebarEffectParams } from '../lib/types';
import { useRouteSidebarEffect } from '../lib/usePageRouteEffect';
import { AIChatContents } from './ai-chat/AIChatContents';

// projectId は UUID 形式（API も UUID 前提）。slug 系のサブセクション
// （例: /projects/ai-matching-results）が projectDetail / projectMemo / projectEdit に
// 誤マッチしてサイドパネルが予期せぬ挙動を起こすのを防ぐため UUID 限定にする。
const UUID_SEGMENT = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const PATH_PATTERN = {
  projectCreate: /^\/projects\/create\/?$/,
  projectEdit: new RegExp(`^/projects/${UUID_SEGMENT}/edit/?$`),
  projectDetail: new RegExp(`^/projects/${UUID_SEGMENT}/?$`),
  projectMemo: new RegExp(`^/projects/${UUID_SEGMENT}/memo/?$`),
} as const;

type RouteSearchParams = {
  threadId?: string;
  fromPdf?: boolean;
};

// state.location.search は型上 unknown 相当のため、必要なフィールドだけ
// `in` + typeof ガードで安全に narrowing する。as キャストは使わない。
const parseRouteSearchParams = (search: unknown): RouteSearchParams | undefined => {
  if (typeof search !== 'object' || search === null) {
    return undefined;
  }
  const threadId =
    'threadId' in search && typeof search.threadId === 'string' ? search.threadId : undefined;
  const fromPdf =
    'fromPdf' in search && typeof search.fromPdf === 'boolean' ? search.fromPdf : undefined;
  return { threadId, fromPdf };
};

const deriveRouteParams = (
  pathname: string,
  search: RouteSearchParams | undefined,
): RouteSidebarEffectParams => {
  // 編集ページは詳細ページにもマッチするため、先に判定する
  if (PATH_PATTERN.projectCreate.test(pathname)) {
    return { route: 'projectCreate', params: { fromPdf: search?.fromPdf ?? false } };
  }
  if (PATH_PATTERN.projectEdit.test(pathname)) {
    return { route: 'projectEdit', params: {} };
  }
  if (PATH_PATTERN.projectMemo.test(pathname)) {
    // メモページでも事業詳細と同じ AI チャットメニューを共有する
    return { route: 'projectDetail', params: { threadId: search?.threadId } };
  }
  if (PATH_PATTERN.projectDetail.test(pathname)) {
    return { route: 'projectDetail', params: { threadId: search?.threadId } };
  }
  return { route: undefined, params: undefined };
};

export const RightSideMenuContents = () => {
  const rightMode = useRouteSidePanelStore((state) => state.mode);
  const isRightOpen = useRouteSidePanelStore((state) => state.isOpen);
  const setOpen = useRouteSidePanelStore((state) => state.setOpen);

  // modeからcontextを取得
  const aiContextMenu = rightMode?.type === 'ai-chat' ? rightMode.context : undefined;

  const setAiContextMenu = (context: AIContext | undefined) => {
    const newMode: SidePanelMode = context ? { type: 'ai-chat', context } : { type: 'ai-chat' };
    setOpen({ isOpen: isRightOpen, mode: newMode });
  };

  //////////////////////////////////////////////////////////////
  // ルート変更によるEffect
  //
  // pathname と search は location 経由で atomic に更新されるため、route は pathname から
  // 派生させる。useMatch / state.matches を使うと navigation 中に pathname だけ先に
  // 更新され、matches が 1 render ぶん古い状態に残ることがあり、その間に open 系の処理
  // が走るとリセット直後に再オープンしてしまう（ITA-2512）。

  const { pathname, routeParams } = useRouterState({
    select: (state) => {
      const pathname = state.location.pathname;
      const search = parseRouteSearchParams(state.location.search);
      return { pathname, routeParams: deriveRouteParams(pathname, search) };
    },
  });

  useRouteSidebarEffect({ pathname, setAiContextMenu, ...routeParams });
  //////////////////////////////////////////////////////////////

  if (rightMode?.type === 'ai-chat') {
    return <AIChatContents aiContextMenu={aiContextMenu} setAiContextMenu={setAiContextMenu} />;
  }

  if (rightMode?.type === 'settlement-statistics-converter.revenue-edit') {
    return <RevenueEditSidePanel />;
  }

  if (rightMode?.type === 'settlement-statistics-converter.expenditure-edit') {
    return <ExpenditureEditSidePanel />;
  }

  if (rightMode?.type === 'settlement-statistics-converter.master') {
    return <MasterCreateSidePanel />;
  }
  if (rightMode?.type === 'master-management.accounting-types') {
    return <AccountingTypesSidePanel />;
  }
  if (rightMode?.type === 'master-management.expenditure-items') {
    return <ExpenditureItemsSidePanel />;
  }
  if (rightMode?.type === 'master-management.organizations') {
    return <OrganizationsSidePanel />;
  }
  if (rightMode?.type === 'tenant-users') {
    return <UserManagementSidePanel />;
  }

  // 決算統計コンバーター財源充当画面で、歳出の作成・編集を行う
  if (rightMode?.type === 'settlement-statistics-converter.revenue-appropriation') {
    return <RevenueAppropriationExpenditureSidePanel />;
  }

  // AIマッチング結果ページから事業情報を表示する（mock 段階）
  if (rightMode?.type === 'ai-matching-project-detail') {
    return <AIMatchingProjectDetailSidePanel />;
  }

  return <AssessmentComparisonSidePanel />;
};
